import React from 'react';
import moment from 'moment';

import borderLeftStyles from '../CustomRow/BorderLeft.module.scss';
import BorderLeft from '../CustomRow/BorderLeft';
import SortableSymbol from '../customheader/SortableSymbol';
import MonitoringStatusCell from '../CustomRow/MonitoringStatusCell';
import { statusOrderingMapping } from './constants';
import TitleCell from '../../impactanalysis/impactsummary/imimpactsummary/CustomCells/TitleCell';

const statusValue = status => (status ? statusOrderingMapping[status] : '0');

const RenderTitleCell = (row, value) => (
    <TitleCell
        title={ row.original.title }
        value={ value || value === 0 ? value.toLocaleString() : '' }
        alignRight
        titleRow={ row.original.titleRow === true }
        error={ row.original.error }
    />
);

const makeColumns = (useDollars = false) => {
    const currencyString = useDollars ? 'Usd' : 'Eur';
    return (
        [
            {
                title: 'Monitoring Status',
                id: 'status',
                type: 'string',
                visible: true,
                minWidth: 200,
                draggable: false,
                hideable: true,
                sortable: true,
                sortMethod: (a, b) => {
                    const valA = statusValue(a.status);
                    const valB = statusValue(b.status);
                    if (valA < valB) { return 1; }
                    if (valA > valB) { return -1; }
                    return 0;
                },
                renderCell: row => (
                    <MonitoringStatusCell
                        colour={ row.value.colour }
                        status={ row.value.status }
                    />
                )
            },
            {
                title: 'CSA',
                id: 'csa',
                type: 'string',
                visible: true,
                minWidth: 70,
                draggable: false,
                hideable: false,
                noPadding: true,
                sortable: true,
                renderCell: (row) => {
                    switch (row.original.reconciliationStatus) {
                        case 'Dispute': {
                            return (
                                <BorderLeft
                                    dispute
                                    styles={ borderLeftStyles }
                                    word={ row.value }
                                />
                            );
                        }
                        case 'Discrepancy': {
                            return (
                                <BorderLeft
                                    discrepancy
                                    styles={ borderLeftStyles }
                                    word={ row.value }
                                />
                            );
                        }
                        default:
                            return (
                                <BorderLeft
                                    styles={ borderLeftStyles }
                                    word={ row.value }
                                />
                            );
                    }
                }
            },
            {
                title: 'Business Date',
                id: 'businessDate',
                type: 'string',
                visible: true,
                minWidth: 120,
                draggable: false,
                hideable: true,
                sortable: true,
                renderCell: row => moment(row.value).format('DD MMM YYYY')
            },
            {
                title: 'Counterparty',
                id: 'counterparty',
                type: 'string',
                visible: true,
                minWidth: 300,
                draggable: false,
                hideable: false,
                sortable: true
            },
            {
                title: 'IM Threshold Collect',
                id: `imThresholdCollect${currencyString}`,
                idWithOutCurrency: 'imThresholdCollect',
                type: 'number',
                visible: true,
                minWidth: 160,
                draggable: false,
                hideable: true,
                sortable: true,
                hasSymbol: true,
                Header: header => (
                    <SortableSymbol
                        gridStyles={ header.column.getGridProps().styles }
                        title={ header.column.title }
                        symbol={ header.column.getGridProps().symbol }
                        alignRight
                    />
                ),
                renderCell: row => RenderTitleCell(row, row.column.getGridProps().symbol === 'EUR' ? row.original.imThresholdCollectEur : row.original.imThresholdCollectUsd)
            },
            {
                title: 'IM Threshold Post',
                id: `imThresholdPost${currencyString}`,
                idWithOutCurrency: 'imThresholdPost',
                type: 'number',
                visible: true,
                minWidth: 160,
                draggable: false,
                hideable: true,
                sortable: true,
                hasSymbol: true,
                Header: header => (
                    <SortableSymbol
                        gridStyles={ header.column.getGridProps().styles }
                        title={ header.column.title }
                        symbol={ header.column.getGridProps().symbol }
                        alignRight
                    />
                ),
                renderCell: row => RenderTitleCell(row, row.column.getGridProps().symbol === 'EUR' ? row.original.imThresholdPostEur : row.original.imThresholdPostUsd)
            },
            {
                title: 'Threshold Level 1',
                id: `monitoringThresholdLevel1${currencyString}`,
                idWithOutCurrency: 'monitoringThresholdLevel1',
                type: 'number',
                visible: true,
                minWidth: 160,
                draggable: false,
                hideable: true,
                sortable: true,
                hasSymbol: true,
                Header: header => (
                    <SortableSymbol
                        gridStyles={ header.column.getGridProps().styles }
                        title={ header.column.title }
                        symbol={ header.column.getGridProps().symbol }
                        alignRight
                    />
                ),
                renderCell: row => RenderTitleCell(row, row.column.getGridProps().symbol === 'EUR' ? row.original.monitoringThresholdLevel1Eur : row.original.monitoringThresholdLevel1Usd)
            },
            {
                title: 'Threshold Level 2',
                id: `monitoringThresholdLevel2${currencyString}`,
                idWithOutCurrency: 'monitoringThresholdLevel2',
                type: 'number',
                visible: true,
                minWidth: 160,
                draggable: false,
                hideable: true,
                sortable: true,
                hasSymbol: true,
                Header: header => (
                    <SortableSymbol
                        gridStyles={ header.column.getGridProps().styles }
                        title={ header.column.title }
                        symbol={ header.column.getGridProps().symbol }
                        alignRight
                    />
                ),
                renderCell: row => RenderTitleCell(row, row.column.getGridProps().symbol === 'EUR' ? row.original.monitoringThresholdLevel2Eur : row.original.monitoringThresholdLevel2Usd)
            },
            {
                title: 'IM Pledge',
                id: `imPledge${currencyString}`,
                idWithOutCurrency: 'imPledge',
                type: 'number',
                visible: true,
                minWidth: 120,
                draggable: true,
                hideable: true,
                hasSymbol: true,
                sortable: true,
                Header: header => (
                    <SortableSymbol
                        gridStyles={ header.column.getGridProps().styles }
                        title={ header.column.title }
                        symbol={ header.column.getGridProps().symbol }
                        alignRight
                    />
                ),
                renderCell: row => RenderTitleCell(row, row.column.getGridProps().symbol === 'EUR' ? row.original.imPledgeEur : row.original.imPledgeUsd)
            },
            {
                title: 'IM Receive',
                id: `imReceive${currencyString}`,
                idWithOutCurrency: 'imReceive',
                type: 'number',
                visible: true,
                minWidth: 120,
                draggable: true,
                hideable: true,
                sortable: true,
                hasSymbol: true,
                Header: header => (
                    <SortableSymbol
                        gridStyles={ header.column.getGridProps().styles }
                        title={ header.column.title }
                        symbol={ header.column.getGridProps().symbol }
                        alignRight
                    />
                ),
                renderCell: row => RenderTitleCell(row, row.column.getGridProps().symbol === 'EUR' ? row.original.imReceiveEur : row.original.imReceiveUsd)
            }
        ]
    );
};

export default makeColumns;
